import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import CataloguesAPI, { Catalog, CatalogItem } from "../../api/CataloguesAPI";
import { BolderText } from "../../components/BolderText/BolderText";
import DressCardDetails from "../../components/DressCardDetails/DressCardDetails";
import DressGrid from "../../components/DressGrid/DressGrid";
import { Modal } from "../../components/Modal/Modal";
import NoData from "../../components/NoData/NoData";
import PageWrapper from "../PageWrapper/PageWrapper";
import FloatingCTAButton from "../../components/FloatingCTAButton/FloatingCTAButton";
// import { BasketItem } from "../../components/BasketItem/BasketItem";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import Confetti from 'react-dom-confetti';
import CatalogDescription from "../../components/CatalogDescription/CatalogDescription";
import CatalogTitle from "../../components/CatalogTitle/CatalogTitle";
import CatalogSubtitle from "../../components/CatalogSubtitle/CatalogSubtitle";
import { datadogRum } from '@datadog/browser-rum';
import { toastService } from "../../services/ToastService";
// import SecondaryButton from "../../components/SecondaryButton/SecondaryButton";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { Check } from "lucide-react";
import LoginFlow from "../../components/LoginFlow/LoginFlow";

function CatalogSingleShowRoomPage() {
  let { someCatalogueId, itemId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const [showSelection, setShowSelection] = useState(false);
  const [catalog, setCatalog] = useState<Catalog | null>();
  const [isLoading, setIsLoading] = useState(false);
  const [isUserActionLoading, setIsUserActionLoading] = useState(false);
  const [activeItem, setActiveItem] = useState<CatalogItem | null>();
  const isLoggedIn = useSelector((state: any) => state.settings.value.isLoggedIn);
  // const basketItems = useSelector((state: any) => state.basket.items);
  const [displayLoginSignUpFlow, setDisplayLoginSignUpFlow] = useState(false);
  const [successfullyImported, setSuccessfullyImported] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const navigate = useNavigate();
  const config = { angle: 90, spread: 360, startVelocity: 40, elementCount: 170, dragFriction: 0.12, duration: 10000, stagger: 3, width: "10px", height: "10px", perspective: "500px", colors: ["#a864fd", "#29cdff", "#78ff44", "#ff718d", "#fdff6a"] };

  useEffect(() => {
    if (!itemId) {
      setActiveItem(null);
    }
  }, [itemId]);

  useEffect(() => {
    const fetchItems = async () => {
      if (!someCatalogueId) return;
      setIsLoading(true);
      try {
        const catalogResponse = await CataloguesAPI.getCatalog(someCatalogueId);
        setCatalog(catalogResponse.data);
        if (itemId) {
          const item = catalogResponse.data.items.find((item: CatalogItem) => item.uuid === itemId);
          setActiveItem(item);
        }
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
      }
    };
  
    fetchItems();
  }, [someCatalogueId, itemId]);

  useEffect(() => {
    const importSuccess = searchParams.get('importSuccess');
    if (importSuccess === 'true') {
      showSuccessPopup();
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleCardClick = (item: CatalogItem) => {
    setActiveItem(item);
    navigate(`/catalogues/${someCatalogueId}/${item.uuid}`);
  };

  const showSuccessPopup = () => {
    datadogRum.addAction('Catalog Successfully imported');
    setShowSelection(false);
    setDisplayLoginSignUpFlow(false);
    setSuccessfullyImported(true);
    setTimeout(() => {
      setShowConfetti(true);
    }, 250);
  }

  const closeSuccessPopup = () => {
    setSuccessfullyImported(false);
    setShowConfetti(false);
  }

  const importCatalog = async () => {
    if (catalog?.items && catalog?.items.length > 0) {
      setIsUserActionLoading(true);
      try {
        await CataloguesAPI.import(someCatalogueId);
        setIsUserActionLoading(false);
        setSearchParams({ importSuccess: 'true' });
      } catch (error) {
        setIsUserActionLoading(false);
        toastService.showToast(t("toast.errors.catalogues.import_failed"), 'error');
      }
    } else {
      toastService.showToast(t("toast.errors.catalogues.not_found"), 'error');
    }
  }

  const handleImportCatalog = async () => {
    datadogRum.addAction('Import Catalog button clicked');  
    if (!isUserActionLoading) {
      if (!isLoggedIn) {
        setShowSelection(false);
        setDisplayLoginSignUpFlow(true);
      } else {
        await importCatalog();
      }
    }
  };
  
  return (
    <PageWrapper>
        <>
          {/* <Banner
            title={t("plans.mission.title")}
            description={t("plans.mission.body")}
            primaryAction={{ label: t("marketplace.catalogues.selection.cta"), onClick: handleImportCatalog }}
            secondaryAction={{ label: t("plans.demo_cta"), onClick: () => window.open('https://calendly.com/eateliers', "_blank") }}
            secondaryText={t("plans.mission.body_secondary")!}
          />
          <br/> */}
          <CatalogTitle text={catalog?.name || ''} clazz={'text-center'}/>
          <CatalogSubtitle text={catalog?.storeName || ''} clazz={'text-center'}/>
          <br/>
          { isLoading ? 
              <div className="space-y-4">
                <Skeleton className="h-12 w-3/4 mx-auto" />
                <Skeleton className="h-4 w-1/2 mx-auto" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="space-y-2">
                      <Skeleton className="h-48 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  ))}
                </div>
              </div>
              :
              activeItem ? <DressCardDetails item={activeItem} backButton={() => navigate(`/catalogues/${someCatalogueId}`)}/> : 
                !someCatalogueId ? (
                  <NoData message={t("marketplace.catalogues.not_found")!} />
                ) : (
                  <div className="space-y-5">
                    {/* <BackButton fn={() => navigate(`/catalogues/showroom`)} text="See More Catalogues" /> */}
                    { catalog?.description && <CatalogDescription description={catalog.description} /> }
                    { catalog?.items && catalog.items.length > 0 ?
                      <DressGrid items={catalog?.items || []} onCardClick={handleCardClick}/>
                      : 
                      <NoData message={t("marketplace.catalogues.not_found")!} />
                    }
                  </div>
              )
          }
          {/* { (basketItems.length > 0) && <FloatingCTAButton text={`${t("marketplace.basket.cta")!}`} fn={() => setShowSelection(true)} alignment="right"/>} */}
          { <FloatingCTAButton 
            text={`${t("marketplace.catalogues.selection.cta")!}`} 
            fn={() => setShowSelection(true)} 
            alignment="right"/>
          }
          { displayLoginSignUpFlow && 
            <Modal canClose modalTitle={t("guest.login.form.title")} onClose={() => setDisplayLoginSignUpFlow(false)}>
              <>
                <p>
                  { t("guest.login.form.description") }.
                </p>
                <ul className="space-y-3 mt-4 mb-6 text-left">
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-gold-500 mt-1 mr-2 flex-shrink-0" />
                    <BolderText text={t("guest.login.form.reason_1")!}/>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-gold-500 mt-1 mr-2 flex-shrink-0" />
                    <BolderText text={t("guest.login.form.reason_2")!}/>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-gold-500 mt-1 mr-2 flex-shrink-0" />
                    <BolderText text={t("guest.login.form.reason_3")!}/>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-gold-500 mt-1 mr-2 flex-shrink-0" />
                    <span>... { t("guest.login.form.reason_others") }</span>
                  </li>
                </ul>
                <LoginFlow callbackFn={importCatalog} skipOnboardingAfterSignup={true} />
              </>
            </Modal> 
          }
          { successfullyImported && 
            <Modal canClose modalTitle={t("marketplace.import.title")} onClose={closeSuccessPopup}>
                <div className="flex flex-col space-y-5">
                  <p>{t("marketplace.import.body")}</p>
                  <div className="flex justify-center">
                    <Confetti active={showConfetti} config={config} />
                    <p className="text-9xl">🎉</p>
                  </div>
                  <div className="flex justify-center space-x-3">
                    {/* <SecondaryButton 
                      text={t("marketplace.import.cta.catalogues")!} fn={() => navigate('/catalogues')} 
                      actionName="Go to Catalogues"
                    /> */}
                    <PrimaryButton 
                      text={t("marketplace.import.cta.order")!} fn={() => navigate('/orders?openForm=true')} 
                      actionName="Go to Orders"
                    />
                  </div>
                </div>
            </Modal>
          }
          <br/>
          {showSelection && (
            <Modal
              canClose
              modalTitle={t("marketplace.catalogues.selection.title")!}
              onClose={() => setShowSelection(false)}
            >
              <div>
                <div className="mb-4">
                  <p>{t("marketplace.catalogues.selection.intro").split('With eAteliers, ')[0]}</p>
                </div>
                <ul className="space-y-3 mb-6 text-left">
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-gold-500 mt-1 mr-2 flex-shrink-0" />
                    <span><b>{t("marketplace.catalogues.selection.benefit.calendar.label")}</b> {t("marketplace.catalogues.selection.benefit.calendar.desc")}</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-gold-500 mt-1 mr-2 flex-shrink-0" />
                    <span><b>{t("marketplace.catalogues.selection.benefit.booking.label")}</b> {t("marketplace.catalogues.selection.benefit.booking.desc")}</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-gold-500 mt-1 mr-2 flex-shrink-0" />
                    <span><b>{t("marketplace.catalogues.selection.benefit.orders.label")}</b> {t("marketplace.catalogues.selection.benefit.orders.desc")}</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-gold-500 mt-1 mr-2 flex-shrink-0" />
                    <span><b>{t("marketplace.catalogues.selection.benefit.networking.label")}</b> {t("marketplace.catalogues.selection.benefit.networking.desc")}</span>
                  </li>
                </ul>
                <div className="flex justify-center">
                  <PrimaryButton
                    text={t("marketplace.catalogues.selection.cta")!}
                    fn={handleImportCatalog}
                    isLoading={isUserActionLoading}
                  />
                </div>
              </div>
            </Modal>
          )}
        </>
    </PageWrapper>
  );
}

export default CatalogSingleShowRoomPage;
